import { RegionShared } from "@1games-ru/region-shared";
import { KeyboardBindType } from "@1games-ru/region-shared/dist/enums/account/settings";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import clsx from "clsx";
import useAppSelector from "@/core/helpers/useAppSelector";
import { useKeyPress } from "@/core/keyPressManager";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import RpcMpProvider from "@/core/rpc-mp-provider/rpc-mp-provider";
import { keyCodeLabel } from "@/shared/constants/keyCodeLabel";
import { RefreshIcon } from "@/shared/icons";
import { Confirm, SoundButton, Tabs } from "@/shared/ui";
import PlayerMenuContent from "@/domains/playerMenu/components/PlayerMenuContent";
import PlayerMenuContentProgress from "@/domains/playerMenu/components/PlayerMenuContentProgress";
import PlayerMenuContentTitle from "@/domains/playerMenu/components/PlayerMenuContentTitle";
import PlayerMenuSettingsControlBind from "@/domains/playerMenu/components/settings/control/PlayerMenuSettingsControlBind";
import useControlSettings from "@/domains/settings/control/hooks/useControlSettings";
import useControlValues from "@/domains/settings/control/hooks/useControlValues";
import { controlSettingsSelector } from "@/domains/settings/control/slice";
import KeyCode from "@/assets/constants/key-code";

enum Tab {
  Basic = "Basic",
  Vehicle = "Vehicle",
  Conversation = "Conversation",
}

export default function PlayerMenuSettingsControlContainer() {
  const { t } = useTranslation("playerMenu");
  const { basicControls, vehicleControls, conversationControls } = useControlValues();
  const { subscribeKeyUp, unsubscribeKeyUp } = useKeyPress();
  const { getControlSettings } = useControlSettings();
  const [selectedBind, setSelectedBind] = useState<KeyboardBindType | -1>(-1); // TODO: если > -1 то выбрано действие для ребинда

  const [isReasignSettings, setIsReasignSettings] = useState(false);
  const [isResetSettings, setIsResetSettings] = useState(false);
  const [isResetKeybind, setIsResetKeybind] = useState<KeyboardBindType | -1>(-1);
  const controlSettings = useAppSelector(controlSettingsSelector);
  const [currTab, setCurrTab] = useState<Tab>(Tab.Basic);

  useEffect(() => {
    if (selectedBind > -1 && !isReasignSettings) {
      const key = "rebindSubscribe";
      const handler = async (keyCode: KeyCode) => {
        if (keyCode === KeyCode.ESCAPE) return;

        setIsReasignSettings(true);
        const response = await RpcMpProvider.callClient<
          typeof RegionShared.Client.Requests.SettingsBindUpdateRequest,
          typeof RegionShared.Client.Responses.SettingsBindUpdateResponse
        >(RegionShared.Client.Events.Account.SettingsBindUpdate, {
          type: selectedBind as KeyboardBindType,
          key: keyCode,
        });
        if (response instanceof RpcMpProviderSuccessResponse) {
          await getControlSettings();
          toast.success(
            `${t(`playerMenu.control.${KeyboardBindType[selectedBind]}`)} переназначен`,
          );
        } else {
          toast.error(response.error.message);
        }

        setSelectedBind(-1);
        setIsReasignSettings(false);
      };

      subscribeKeyUp(key, handler);

      return () => {
        unsubscribeKeyUp(key);
      };
    }

    return () => {};
  }, [selectedBind, isReasignSettings, getControlSettings, subscribeKeyUp, t, unsubscribeKeyUp]);

  const resetToDefaultHandler = useCallback(async () => {
    setIsResetSettings(true);
    const response = await RpcMpProvider.callClient(
      RegionShared.Client.Events.Account.SettingsBindDefault,
      {},
    );
    if (response instanceof RpcMpProviderSuccessResponse) {
      await getControlSettings();
    } else {
      toast.error("Ошибка сброса к стандартным настройкам");
    }
    setIsResetSettings(false);
  }, []);

  const resetKeybindHandler = useCallback(
    async (keybind: KeyboardBindType) => {
      setIsResetKeybind(keybind);

      const response = await RpcMpProvider.callClient<
        typeof RegionShared.Client.Requests.SettingsBindUpdateRequest,
        typeof RegionShared.Client.Responses.SettingsBindUpdateResponse
      >(RegionShared.Client.Events.Account.SettingsBindUpdate, {
        type: keybind,
        key: controlSettings[keybind].defaultKey,
      });

      if (response instanceof RpcMpProviderSuccessResponse) {
        await getControlSettings();
        toast.success(
          `Успешный сброс ${t(`playerMenu.control.${KeyboardBindType[keybind]}`)} к стандартным настройкам`,
        );
      } else {
        toast.error(
          `Ошибка сброса ${t(`playerMenu.control.${KeyboardBindType[keybind]}`)} к стандартным настройкам`,
        );
      }

      setIsResetKeybind(-1);
    },
    [controlSettings],
  );

  const tabs = useMemo(
    () => [
      { value: Tab.Basic, label: t(`playerMenu.tab.Control${Tab.Basic}`) },
      { value: Tab.Vehicle, label: t(`playerMenu.tab.Control${Tab.Vehicle}`) },
      { value: Tab.Conversation, label: t(`playerMenu.tab.Control${Tab.Conversation}`) },
    ],
    [],
  );

  const binds = useMemo(() => {
    switch (currTab) {
      case Tab.Basic:
        return basicControls;
      case Tab.Vehicle:
        return vehicleControls;
      case Tab.Conversation:
        return conversationControls;
      default:
        return [];
    }
  }, [basicControls, conversationControls, currTab, vehicleControls]);

  const header = (
    <div>
      <PlayerMenuContentTitle>{t("playerMenu.tabTitle.Control")}</PlayerMenuContentTitle>
      <Tabs tabs={tabs} value={currTab} onClick={(value) => setCurrTab(value as Tab)} />
    </div>
  );

  const footer = (
    <div className="flex items-center justify-start">
      <SoundButton
        variant="soft"
        color="gray"
        className="h-[2.75rem] gap-2.5"
        loading={isResetSettings}
        disabled={isResetSettings}
        onClick={resetToDefaultHandler}
      >
        <RefreshIcon size={18} className="text-white/50" />
        Сбрость к стандартным
      </SoundButton>
    </div>
  );

  return (
    <PlayerMenuContent header={header} footer={footer}>
      <Confirm
        open={selectedBind > -1}
        onConfirm={() => setSelectedBind(-1)}
        onCancel={() => setSelectedBind(-1)}
        confirmText="Отмена"
        showCancel={false}
        pending={isReasignSettings}
        title={t("playerMenu.tabTitle.Control")}
      >
        {controlSettings[selectedBind] ? (
          <div>
            Нажмите на клавишу, которую хотите использовать для{" "}
            {t(`playerMenu.keyboardBindType.${KeyboardBindType[selectedBind]}`)} вместо{" "}
            <span
              className={clsx(
                "h-[1.25rem] w-fit min-w-[1.25rem] px-1 flex items-center justify-center rounded-[0.325rem]",
                "text-14 leading-3 font-bold",
                "text-dark-900 bg-white uppercase",
              )}
            >
              {keyCodeLabel[controlSettings[selectedBind].key]}
            </span>
          </div>
        ) : (
          `Нажмите на клавишу, которую хотите назначить для ${t(`playerMenu.keyboardBindType.${KeyboardBindType[selectedBind]}`)}`
        )}
      </Confirm>
      {/* {binds.length > 0 ? ( */}
      <div className="grid grid-cols-2 gap-y-2 gap-x-5">
        {binds.map(([key, value]) => (
          <PlayerMenuSettingsControlBind
            key={key}
            keyboardBindType={key}
            keyCode={value.key}
            description={t(`playerMenu.keyboardBindType.${KeyboardBindType[key]}`)}
            onClick={setSelectedBind}
            onReset={resetKeybindHandler}
            isResetting={key === isResetKeybind}
            // isResetting
          />
        ))}
      </div>
      {/* ) : (
        <PlayerMenuContentProgress />
      )} */}
    </PlayerMenuContent>
  );
}
